import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLoder from '../../components/PageLoader';
import "../../styles/ConsultantEvaluation.css";
import './compact-eval.css';

export default function EditSelfEvaluation() {
     const { id } = useParams();
     const navigate = useNavigate();
     const baseUrl = process.env.REACT_APP_BASE_URL;
     const [loading, setLoading] = useState(true);
     const [saving, setSaving] = useState(false);
     const [questions, setQuestions] = useState([]);
     // helper to map points to pastel background colors
     const getBgColor = (points) => {
          switch (String(points)) {
               case '3':
                    return '#e6ffed'; // pastel green
               case '2':
                    return '#fff8e6'; // pastel orange
               case '1':
                    return '#ffecec'; // pastel red
               case '0':
                    return '#f3f3f3'; // light gray
               default:
                    return '';
          }
     };

     const getTextColor = (points) => {
          switch (String(points)) {
               case '3':
                    return '#1f7a1f';
               case '2':
                    return '#a05a00';
               case '1':
                    return '#b30000';
               case '0':
                    return '#666666';
               default:
                    return 'inherit';
          }
     };
     const [form, setForm] = useState({ emp_code: '', emp_name: '', quarter: '', centre: '', created_by: '', specialty: '', timing: '', year: '', mobile: '', employment_type: '', doj: '', vdc_exp: '', dept: '', overall_comment: '', is_submitted: 'N' });
     const [details, setDetails] = useState({});
     const [message, setMessage] = useState(null);
     const [detailErrors, setDetailErrors] = useState({});

     const quarterLabel = (q) => {
          if (!q) return '';
          switch (q.toString().toUpperCase()) {
               case 'Q1': return 'Jan - Mar';
               case 'Q2': return 'Apr - Jun';
               case 'Q3': return 'Jul - Sep';
               case 'Q4': return 'Oct - Dec';
               default: return q;
          }
     };

     useEffect(() => {
          let mounted = true;

          const load = async () => {
               setLoading(true);
               try {
                    const token = localStorage.getItem('token');
                    const headers = { Accept: 'application/json' };
                    if (token) headers.Authorization = `Bearer ${token}`;

                    // fetch evaluation
                    const evalUrl = `${baseUrl}/getConsultantEvaluation/${id}`;
                    const res = await fetch(evalUrl, { method: 'GET', headers });
                    const json = await res.json();

                    let ev = null;
                    // build a serverDetailsMap to use when merging with questions
                    let serverDetailsMap = {};
                    if (json && json.evaluation) {
                         ev = json.evaluation;
                         const arr = Array.isArray(json.details) ? json.details : [];
                         serverDetailsMap = arr.reduce((acc, d) => ({ ...acc, [d.question_id ?? d.id]: { question_id: d.question_id ?? d.id, points: d.points, comments: d.comments ?? '' } }), {});
                    } else if (Array.isArray(json) && json.length) {
                         ev = json[0];
                         const arr = Array.isArray(json[0].details) ? json[0].details : [];
                         serverDetailsMap = arr.reduce((acc, d) => ({ ...acc, [d.question_id ?? d.id]: { question_id: d.question_id ?? d.id, points: d.points, comments: d.comments ?? '' } }), {});
                    } else if (json && typeof json === 'object') {
                         ev = json;
                         const arr = Array.isArray(json.details) ? json.details : [];
                         serverDetailsMap = arr.reduce((acc, d) => ({ ...acc, [d.question_id ?? d.id]: { question_id: d.question_id ?? d.id, points: d.points, comments: d.comments ?? '' } }), {});
                    }

                    // set details map from server first
                    setDetails(serverDetailsMap);

                    if (ev) {
                         const pick = (obj, keys, fallback = '') => {
                              for (const k of keys) if (obj && (obj[k] !== undefined && obj[k] !== null)) return obj[k];
                              return fallback;
                         };

                         const computed = {
                              emp_code: pick(ev, ['emp_code', 'employee_code', 'emp_code', 'empId', 'emp_id'], ''),
                              emp_name: pick(ev, ['emp_name', 'employee_name', 'comp_name', 'name', 'user_name'], ''),
                              quarter: pick(ev, ['quarter'], ''),
                              centre: pick(ev, ['centre', 'location_name', 'centre_name', 'location'], ''),
                              created_by: pick(ev, ['created_by', 'created_by_id', 'createdBy'], ''),
                              specialty: pick(ev, ['specialty', 'speciality'], ''),
                              timing: pick(ev, ['timing', 'shift'], ''),
                              year: pick(ev, ['year'], ''),
                              mobile: pick(ev, ['mobile', 'mobile_no', 'mobile_number', 'phone', 'contact_number', 'contact'], ''),
                              employment_type: pick(ev, ['employment_type', 'employment', 'emp_type'], ''),
                              doj: pick(ev, ['doj', 'date_of_joining', 'join_date', 'joined_on'], ''),
                              vdc_exp: pick(ev, ['vdc_exp', 'vdc_experience'], ''),
                              overall_comment: pick(ev, ['overall_comment', 'overallComment', 'overall_comments'], ''),
                              dept: pick(ev, ['dept', 'dept_name', 'department', 'department_name'], ''),
                              is_submitted: pick(ev, ['is_submitted', 'submitted', 'submitted_flag'], 'N'),
                              submitted_on: pick(ev, ['submitted_on', 'submitted_at', 'submitted_date', 'updated_at', 'created_at'], ''),
                         };

                         setForm(computed);

                         // If key profile fields are missing from the evaluation, try to fetch employee details
                         const needs = (!computed.mobile || computed.mobile === '') || (!computed.dept || computed.dept === '') || (!computed.doj || computed.doj === '') || (!computed.employment_type || computed.employment_type === '');
                         if (needs && computed.emp_code) {
                              try {
                                   const eUrl = `${baseUrl}/getEmployeeDetails/${encodeURIComponent(computed.emp_code)}`;
                                   const eHeaders = { Accept: 'application/json' };
                                   const token2 = localStorage.getItem('token');
                                   if (token2) eHeaders.Authorization = `Bearer ${token2}`;
                                   const eres = await fetch(eUrl, { method: 'GET', headers: eHeaders });
                                   const edata = await eres.json();
                                   if (Array.isArray(edata) && edata.length > 0) {
                                        const emp = edata[0];
                                        setForm(f => ({
                                             ...f,
                                             mobile: f.mobile || emp.mobile || emp.mobile_no || emp.contact || '',
                                             doj: f.doj || emp.doj || emp.date_of_joining || emp.join_date || '',
                                             dept: f.dept || emp.dept_name || emp.department || emp.dept || '',
                                             employment_type: f.employment_type || emp.employment_type || emp.emp_type || emp.employment || '',
                                        }));
                                   }
                              } catch (ex) {
                                   console.debug('Failed to fetch employee details', ex);
                              }
                         }
                    }

                    // fetch questions list (to render selects in correct order)
                    const qUrl = `${baseUrl}/getConsultantEvaluationQuestions`;
                    const qres = await fetch(qUrl, { method: 'GET', headers });
                    const qjson = await qres.json();
                    if (Array.isArray(qjson)) {
                         setQuestions(qjson);
                         // ensure details has entries for all questions using serverDetailsMap
                         const init = (qjson || []).reduce((acc, q) => {
                              const key = q.id;
                              const existing = serverDetailsMap[key] || {};
                              acc[key] = { question_id: key, points: existing.points === undefined ? '' : existing.points, comments: existing.comments || '' };
                              return acc;
                         }, {});
                         setDetails(init);
                    }
               } catch (err) {
                    console.error(err);
                    setMessage({ type: 'error', text: 'Failed to load evaluation' });
               } finally {
                    if (mounted) setLoading(false);
               }
          };

          load();
          return () => { mounted = false; };
     }, [baseUrl, id]);

     // auto-hide toast messages after 3s
     useEffect(() => {
          if (!message) return;
          const t = setTimeout(() => setMessage(null), 3000);
          return () => clearTimeout(t);
     }, [message]);

     function handleFieldChange(e) {
          const { name, value } = e.target;
          setForm(f => ({ ...f, [name]: value }));
     }

     function handleDetailChange(questionId, field, value) {
          setDetails(d => ({ ...d, [questionId]: { ...d[questionId], [field]: value } }));
     }

     async function handleSubmit(e, finalSubmit = false) {
          if (e && e.preventDefault) e.preventDefault();
          setMessage(null);
          setSaving(true);
          // when doing final submit, enforce that any question marked 1 or 0 has comments
          if (finalSubmit) {
               const errors = {};
               Object.entries(details || {}).forEach(([qid, it]) => {
                    const pts = it && (it.points === '' || it.points == null) ? '' : it.points;
                    if (pts !== '') {
                         const n = Number(pts);
                         if (n === 1 || n === 0) {
                              const c = (it.comments || '').toString().trim();
                              if (!c) errors[qid] = 'Comments required for this selection';
                         }
                    }
               });
               if (Object.keys(errors).length > 0) {
                    setDetailErrors(errors);
                    setMessage({ type: 'error', text: 'Please fill all mandatory fields.' });
                    setSaving(false);
                    return;
               } else {
                    setDetailErrors({});
               }
          }
          try {
               const token = localStorage.getItem('token');
               const headers = { 'Content-Type': 'application/json' };
               if (token) headers.Authorization = `Bearer ${token}`;

               const payloadForm = { ...form };
               if (finalSubmit) payloadForm.is_submitted = 'Y';

               const payload = { ...payloadForm, details: Object.values(details) };
               // compute total_points
               payload.total_points = Object.values(details).reduce((s, it) => s + (parseInt(it.points, 10) || 0), 0);

               const url = `${baseUrl}/updateConsultantEvaluation/${id}`;
               const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
               const json = await res.json();
               if (res.ok) {
                    setMessage({ type: 'success', text: 'Submitted successfully' });
                    if (finalSubmit) {
                         setForm(f => ({ ...f, is_submitted: 'Y', submitted_on: new Date().toISOString() }));
                         // redirect to list page after final submission
                         navigate('/consultant-evaluations', { state: { message: { type: 'success', text: 'Submitted successfully' } } });
                    } else {
                         navigate(`/view-self-evaluations/${id}`);
                    }
               } else {
                    setMessage({ type: 'error', text: json.error || JSON.stringify(json) });
               }
          } catch (err) {
               console.error(err);
               setMessage({ type: 'error', text: 'Request failed' });
          } finally {
               setSaving(false);
          }
     }

     function handleFinalSubmit() {
          // direct final submit without confirmation; validation handled in handleSubmit
          handleSubmit(null, true);
     }

     if (loading) return <div className="text-center py-4"><PageLoder /></div>;

     return (
          <div className="consultant-eval compact">

               {/* Toast message */}
               {message && (
                    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
                         <div style={{ padding: '10px 14px', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', background: message.type === 'error' ? '#f8d7da' : '#d4edda', color: message.type === 'error' ? '#721c24' : '#155724', border: message.type === 'error' ? '1px solid #f5c6cb' : '1px solid #c3e6cb' }}>
                              {message.text}
                         </div>
                    </div>
               )}

               <form onSubmit={handleSubmit}>
                    <div className="row">
                         <div className="col-sm-4"><h2>Edit Self Evaluation</h2></div>
                         <div className="col-sm-8">
                              <div className="row">

                                   <input name="emp_code" type="hidden" value={form.emp_code} readOnly />
                                   <input name="centre" type="hidden" value={form.centre ?? ''} readOnly />
                                   <input name="specialty" type="hidden" value={form.specialty ?? ''} onChange={handleFieldChange} />
                                   <input name="timing" type="hidden" value={form.timing ?? ''} onChange={handleFieldChange} />
                                   <input name="emp_name" type="hidden" value={form.emp_name} readOnly />

                                   <div className="col-sm-2">
                                        <select name="quarter" value={form.quarter} disabled>
                                             <option value="">Select quarter</option>
                                             <option value="Q1">Jan - Mar</option>
                                             <option value="Q2">Apr - Jun</option>
                                             <option value="Q3">Jul - Sep</option>
                                             <option value="Q4">Oct - Dec</option>
                                        </select>
                                   </div>
                                   <div className="col-sm-2">
                                        <select name="year" value={form.year} disabled>
                                             <option value="">Select year</option>
                                             {Array.from({ length: 5 }, (_, i) => {
                                                  const y = new Date().getFullYear() - i;
                                                  return <option key={y} value={y}>{y}</option>;
                                             })}
                                        </select>
                                   </div>

                                   {/* Submitted flag hidden in compact/edit view */}
                              </div>
                         </div>
                    </div>
                    <div className="question-list">
                         {questions.length === 0 && <div>No questions available.</div>}
                         {questions.map((q, idx) => (
                              <div key={q.id} className="question-item">
                                   <div className="q-index">{q.display_order || idx + 1}.</div>
                                   <div className="question-text">{q.question_text}</div>

                                   <div className="points-col">
                                        <label className="points-label">
                                             <select
                                                  value={(details[q.id]?.points === '' || details[q.id]?.points == null) ? '' : details[q.id].points.toString()}
                                                  onChange={(e) => {
                                                       const val = e.target.value;
                                                       if (val === '') handleDetailChange(q.id, 'points', '');
                                                       else handleDetailChange(q.id, 'points', parseInt(val, 10));
                                                       // if selected Non Satisfactory (1) or Nothing is done (0) and comments empty, show toast and mark detail error
                                                       try {
                                                            const n = val === '' ? '' : Number(val);
                                                            const existingComments = (details[q.id]?.comments || '').toString().trim();
                                                            setDetailErrors(prev => {
                                                                 const next = { ...prev };
                                                                 if ((n === 1 || n === 0) && !existingComments) {
                                                                      next[q.id] = 'Comments required for this selection';
                                                                      setMessage({ type: 'error', text: 'Please add comments for items marked Non Satisfactory or Nothing is done.' });
                                                                 } else {
                                                                      // clear any existing error for this row when selection is not 1/0 or comments exist
                                                                      delete next[q.id];
                                                                 }
                                                                 return next;
                                                            });
                                                       } catch (e) { /* ignore */ }
                                                  }}
                                                  className="points-input"
                                                  style={{ backgroundColor: getBgColor(details[q.id]?.points), color: getTextColor(details[q.id]?.points) }}
                                             >
                                                  <option value=""> Select Points</option>
                                                  <option value="3"> Good</option>
                                                  <option value="2"> Satisfactory</option>
                                                  <option value="1"> Non Satisfactory</option>
                                                  <option value="0"> Nothing is done</option>
                                             </select>
                                        </label>
                                   </div>

                                   <div className="comments-col">
                                        <input
                                             value={details[q.id]?.comments ?? ''}
                                             onChange={(e) => { handleDetailChange(q.id, 'comments', e.target.value); setDetailErrors(prev => { const next = { ...prev }; delete next[q.id]; return next; }); }}
                                             placeholder="Comments / Description"
                                             className="comments-input"
                                             style={{ backgroundColor: getBgColor(details[q.id]?.points), borderColor: detailErrors[q.id] ? '#c74343' : undefined, maxWidth: '300px' }}
                                        />
                                        {(
                                             (String(details[q.id]?.points) === '1' || String(details[q.id]?.points) === '0') &&
                                             (!(details[q.id]?.comments || '').toString().trim())
                                        ) && (
                                                  <div style={{ color: '#c74343', fontSize: 12, marginTop: 6 }}>This comment is required for the selected rating.</div>
                                             )}
                                   </div>
                              </div>
                         ))}

                         <div className="question-item" style={{ fontWeight: 700 }}>
                              <div className="q-index"></div>
                              <div className="question-text" >
                                   <span style={{ float: 'right !important' }}> Total Points </span></div>
                              <div className="points-col" >
                                   <input name="total_points"
                                        value={Object.values(details).reduce((s, it) => {
                                             const v = parseInt(it.points, 10);
                                             return s + (Number.isNaN(v) ? 0 : v);
                                        }, 0)} readOnly className="points-input" />
                              </div>
                              <div className="comments-col"></div>
                         </div>
                    </div>

                    {form.is_submitted !== 'Y' ? (
                         <div className="form-actions">
                              <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Saving…' : 'Save as Draft'}</button>
                              <button type="button" className="btn info" style={{ marginLeft: 8 }} onClick={handleFinalSubmit} disabled={saving}>{saving ? 'Saving…' : 'Submit'}</button>
                         </div>
                    ) : (
                         <div className="form-submitted" style={{ padding: '12px 0', color: '#0b6', fontWeight: 600 }}>
                              {(() => {
                                   const d = form.submitted_on || form.submitted_at || form.updated_at || form.created_at || '';
                                   try {
                                        if (d) return `Submitted on ${new Date(d).toLocaleString()}`;
                                   } catch (e) {
                                        // ignore
                                   }
                                   return 'Submitted';
                              })()}
                         </div>
                    )}
               </form>
          </div>
     );
}
